import { IMat4, Mat4 } from "@base/math/Mat4";
import { MathUtil } from "@base/math/MathUtil";
import { IVec3, Vec3 } from "@base/math/Vec3";
import { SunSailMath } from "@biz/items/sunSail/logic/SunSailMath";
import { TriangleHelper } from "@biz/items/sunSail/logic/TriangleHelper";

// export interface IClothPoints {
//   net: IVec3[];
//   mount: IVec3[];
// }

// export function calcClothPoints(sunSail: ISunSailData, clothIndex: number, options: INetPositionOptions | undefined): IClothPoints {
//   const net = SunSailLogic.calcNetPositions(sunSail, clothIndex, options);
//   const mount = SunSailLogic.calcMountPositions(sunSail, SunSailLogic.getAxleIndex(sunSail.sunSailType, clothIndex), options);

//   return {
//     net,
//     mount
//   };
// }

export function calcKnotMidMatrix(p: IVec3, netFrom: IVec3, netTo: IVec3, mountPos: IVec3): IMat4 {
  const pToAxleDir = Vec3.dir(p, mountPos);
  const netDir = Vec3.dir(netFrom, netTo);
  const plateMatrix = Mat4.createBasis(Vec3.crossNormalized(pToAxleDir, netDir), netDir, pToAxleDir, p);
  return plateMatrix
}

export function calcSnapShackleMatrix(p: IVec3, netFrom: IVec3, netTo: IVec3, mountPos: IVec3): IMat4 {
  const pToAxleDir = Vec3.dir(mountPos, p);
  const netDir = Vec3.dir(netFrom, netTo);
  const plateMatrix = Mat4.createBasis(Vec3.crossNormalized(pToAxleDir, netDir), netDir, pToAxleDir, p);
  return plateMatrix
}

export function calcPointMountMatrix(p: IVec3, netFrom: IVec3, netTo: IVec3, mountPos: IVec3): IMat4 {
  const pToAxleDir = Vec3.dir(mountPos, p);
  const netDir = Vec3.dir(netFrom, netTo);
  const plateMatrix = Mat4.createBasis(Vec3.crossNormalized(pToAxleDir, netDir), netDir, pToAxleDir, mountPos);
  return plateMatrix
}

export function calcPointMountMatrixNew(p: IVec3, netFrom: IVec3, netTo: IVec3, mountPos: IVec3): IMat4 {
  const pToAxle = Vec3.dirLen(mountPos, p);
  const pToAxleDir = pToAxle.len < 0.00001 ? Vec3.normalized(Vec3.add(mountPos, Vec3.add(Vec3.to(mountPos, netFrom), Vec3.to(mountPos, netTo)))) : pToAxle.dir;
  const netDir = Vec3.dir(netFrom, netTo);
  const right = Vec3.crossNormalized(pToAxleDir, netDir);
  const plateMatrix = Mat4.createBasis(right, Vec3.crossNormalized(right, pToAxleDir), pToAxleDir, mountPos);
  MathUtil.checkNaN([pToAxleDir, netDir, right], "calcPointMountMatrixNew")
  MathUtil.checkNaN(plateMatrix[11], "calcPointMountMatrixNew NAN")
  return plateMatrix
}

export function calcSailPlateMatrixAxle(p: IVec3, netFrom: IVec3, netTo: IVec3): IMat4 {
  const halfVector = TriangleHelper.calcHalfVector(p, netFrom, netTo);
  const info = calcSailPlateInfo("00E13001416"); // BigPlate
  const platePos = Vec3.addScaled(p, halfVector, info.distFromPoint);

  const axlePoint = SunSailMath.calcNearestAxlePoint(netFrom, netTo, p);
  const pToAxleDir = Vec3.dir(p, axlePoint);
  const netDir = Vec3.dir(netFrom, netTo);
  // const plateMatrix = Mat4.createBasis(pToAxleDir, netDir, Vec3.crossNormalized(pToAxleDir, netDir), platePos);
  const plateMatrix = Mat4.createBasis(pToAxleDir, Vec3.negate(Vec3.crossNormalized(pToAxleDir, netDir)), netDir, platePos);
  // const plateMatrix = Mat4.createBasis(pToAxleDir, Vec3.crossNormalized(pToAxleDir, netDir), netDir, platePos);
  // const plateLeft = platePos

  return plateMatrix
}

export function calcSailPlateMatrixHalf(p: IVec3, netFrom: IVec3, netTo: IVec3, cornerArticle: string): IMat4 {

  const halfVector = TriangleHelper.calcHalfVector(p, netFrom, netTo);
  const info = calcSailPlateInfo(cornerArticle);
  const platePos = Vec3.addScaled(p, halfVector, info.distFromPoint);

  const up = Vec3.negate(Vec3.crossNormalized(halfVector, Vec3.dir(netFrom, netTo)));
  const rightDir = Vec3.crossNormalized(up, halfVector);
  const plateMatrix = Mat4.createBasis(halfVector, up, Vec3.negate(rightDir), platePos);

  return plateMatrix
}

export function calcSailPlatePoints(plateMatrix: IMat4, cornerArticle: string): { back: IVec3; front: IVec3; left: IVec3; right: IVec3; } {
  const info = calcSailPlateInfo(cornerArticle);
  const back = Vec3.applyMatrix4(info.offsetBack, plateMatrix);
  const front = Vec3.applyMatrix4(info.offsetFront, plateMatrix);
  const left = Vec3.applyMatrix4(info.offsetLeft, plateMatrix);
  const right = Vec3.applyMatrix4(info.offsetRight, plateMatrix);
  return {
    back,
    front,
    left,
    right
  }
}

export function calcSailPlateSailPoint(plateMatrix: IMat4, cornerArticle: string, offset?: IVec3): IVec3 {
  const info = calcSailPlateInfo(cornerArticle);
  const p = offset ? Vec3.add(info.sail, offset) : info.sail;
  return Vec3.applyMatrix4(p, plateMatrix);
}

export function calcSailPlateInfo(cornerArticle: string): { distFromPoint: number; offsetLeft: IVec3; offsetRight: IVec3, offsetFront: IVec3, offsetBack: IVec3, sail: IVec3 } {
  switch (cornerArticle) {
    case "00E13001416": // BigPlate
      return {
        distFromPoint: 0.02,
        offsetFront: { x: 0.05, y: 0, z: 0 },
        offsetBack: { x: 0.02, y: 0, z: 0 },
        offsetLeft: { x: -0.025, y: 0, z: 0.135 },
        offsetRight: { x: -0.025, y: 0, z: -0.135 },
        sail: { x: -0.0, y: 0, z: 0.0 }
      }
    case "00E13001665": { // OrcaPlate
      return {
        distFromPoint: 0.045, // 0.045,
        offsetFront: { x: 0.10, y: 0, z: 0.0 },
        offsetBack: { x: -0.03, y: 0, z: 0 },
        offsetLeft: { x: 0.005, y: 0, z: 0.075 },
        offsetRight: { x: 0.005, y: 0, z: -0.075 },
        sail: { x: -0.045, y: 0, z: 0 }
      }
    }
    case "00E13001637": { // D_Ring25
      return {
        distFromPoint: 0.025,
        offsetFront: { x: 0.05, y: 0, z: 0 },
        offsetBack: { x: -0.0056, y: 0, z: 0 },
        offsetLeft: { x: -0.0055, y: 0, z: 0.02 },
        offsetRight: { x: -0.0055, y: 0, z: -0.02 },
        sail: { x: -0.025, y: 0, z: 0 }
      }
    }
    case "00E13001638": { // D_Ring40
      return {
        distFromPoint: 0.040,
        offsetFront: { x: 0.05, y: 0, z: 0 },
        offsetBack: { x: -0.0071, y: 0, z: 0 },
        offsetLeft: { x: -0.007, y: 0, z: 0.03 },
        offsetRight: { x: -0.007, y: 0, z: -0.03 },
        sail: { x: -0.04, y: 0, z: 0 }
      }
    }
    case "00E13001417": { // O-ring
      return {
        distFromPoint: 0.018,
        offsetFront: { x: 0.05, y: 0, z: 0 },
        offsetBack: { x: 0.014, y: 0, z: 0 },
        offsetLeft: { x: 0.015, y: 0, z: 0.02 },
        offsetRight: { x: 0.015, y: 0, z: -0.02 },
        sail: { x: -0.018, y: 0, z: 0 }
      }
    }
    case "00E13001951": { // OrcaRing
      return {
        distFromPoint: 0.018,
        offsetFront: { x: 0.05, y: 0, z: 0 },
        offsetBack: { x: 0.014, y: 0, z: 0 },
        offsetLeft: { x: 0.015, y: 0, z: 0.02 },
        offsetRight: { x: 0.015, y: 0, z: -0.02 },
        sail: { x: -0.018, y: 0, z: 0 }
      }
    }
  }
  return { // Schlaufe
    distFromPoint: 0.003,
    offsetFront: { x: 0.05, y: 0, z: 0 },
    offsetBack: { x: -0.001, y: 0, z: 0 },
    offsetLeft: { x: -0.0, y: 0, z: 0.02 },
    offsetRight: { x: -0.0, y: 0, z: -0.02 },
    sail: { x: -0.003, y: 0, z: 0 }
  }
}